import { Client } from '@microsoft/microsoft-graph-client';
import { IAppContext } from 'App/AppContext';
import { Site } from 'microsoft-graph';
import { apiGetUniqueSites } from 'services/Api/listService';
import { apiRequest } from 'services/Auth/authConfig';
import logger from 'services/Logging/logService';
import AppError from 'utils/appError';
import { execGet } from './graphServiceCore';

//
// Sharepoint Sites
//
export const graphGetSiteRoot = async (client: Client): Promise<Site> => {
  try {
    const site = await execGet(client.api(`/sites/root`));

    return site;
  } catch (err) {
    throw AppError.fromGraphError(err);
  }
};

export const graphGetSite = async (client: Client, siteId: string): Promise<Site> => {
  try {
    const site = await execGet(client.api(`/sites/${siteId}`));

    return site;
  } catch (err) {
    throw AppError.fromGraphError(err);
  }
};

export const graphGetSiteByWebUrl = async (client: Client, webUrl: string): Promise<Site | undefined> => {
  try {
    let relUrl = webUrl.replace('/sites/', ':/sites/');
    if (relUrl.startsWith('https://')) relUrl = relUrl.substring(8);
    const site = await execGet(client.api(`/sites/${relUrl}`));

    return site;
  } catch {
    return undefined;
  }
};

export const graphGetSites = async (client: Client, text: string): Promise<Site[]> => {
  try {
    let relSite;
    let urlSite;
    let searchSites;
    let idSite;

    if (text && text.startsWith('https://')) text = text.substring(8);

    try {
      searchSites = await execGet(client.api(`/sites?search=*${text}*`));
      if (!searchSites || !searchSites.value) {
        searchSites = { value: [] };
      }
    } catch (err) {
      searchSites = { value: [] };
      const appErr = AppError.fromGraphError(err);
      if (appErr.code !== 'itemNotFound' && appErr.code !== 'accessDenied') {
        logger.debug('graphGetSites', err);
        throw err;
      }
    }

    if (searchSites.value.length === 0) {
      //when the search did not return any results and no error, try to get the site based on the complete site name
      try {
        const root = await graphGetSiteRoot(client);
        if (root) {
          const host = root.siteCollection?.hostname;
          if (host) {
            relSite = await execGet(client.api(`/sites/${host}:/sites/${text}`));
          }
        }
      } catch {
        //ignore
      }

      //When the search did not return any results, try to get the site based on the complete site url
      //This is the only option for guest users.
      //This method (sometimes) also works to search on site Id.
      if (!relSite) {
        urlSite = await graphGetSiteByWebUrl(client, text);
      }

      //when the search did not return any results, try to get the site based on the complete site id
      //this is the only option for admins that configure selected sites and want them to be available in ISOPlanner
      if (!urlSite) {
        try {
          idSite = await graphGetSite(client, text);
        } catch {
          //ignore
        }
      }
    }

    //combine sites
    const allSites: Site[] = [];
    if (searchSites.value.length > 0) {
      allSites.push(...searchSites.value);
    }
    if (relSite) {
      allSites.push(relSite);
    }
    if (urlSite) {
      allSites.push(urlSite);
    }
    if (idSite) {
      allSites.push(idSite);
    }

    return allSites;
  } catch (err) {
    throw AppError.fromGraphError(err);
  }
};

export const graphGetSuggestedSites = async (client: Client, appContext: IAppContext): Promise<Site[]> => {
  // return a list with:
  // 1. top 3 of the unique sites in ISOPlanner
  // 2. the followed sites in SharePoint
  // 3. the top 10 of all sites in SharePoint

  try {
    const top: number = 10;
    let followedSites;
    let allSites;
    const uniqueSites: Site[] = [];
    let followedSitesError: boolean = false;
    let allSitesError: boolean = false;
    let uniqueSitesError: boolean = false;

    try {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const siteIds = await apiGetUniqueSites(accessToken);

      for (let i = 0; i < siteIds.length; i++) {
        try {
          const site = await graphGetSite(client, siteIds[i]);
          if (site) {
            uniqueSites.push(site);
          }
        } catch {
          continue;
        }
      }
    } catch {
      uniqueSitesError = true;
    }

    try {
      followedSites = await execGet(client.api(`/me/followedSites`));
    } catch {
      followedSitesError = true;
    }

    try {
      allSites = await execGet(client.api(`/sites?search=*&top=${top}`));
    } catch {
      allSitesError = true;
    }

    const allSitesTotal: Site[] = [];

    if (allSitesError && followedSitesError && uniqueSitesError) {
      logger.debug('graphGetSuggestedSites: Could not access sites, followed sites and unique sites');

      return [];
    } else {
      // add top 3 unique sites
      allSitesTotal.push(...uniqueSites.slice(0, 3));

      // add followed sites
      if (!followedSitesError && followedSites.value) {
        let followedSites1: Site[] = followedSites.value;
        followedSites1 = followedSites1.filter((s) => allSitesTotal.findIndex((fs) => s.id === fs.id) === -1);
        allSitesTotal.push(...followedSites1);
      }

      // add all sites
      if (!allSitesError && allSites.value) {
        let allSites1: Site[] = allSites.value;
        allSites1 = allSites1.filter((s) => allSitesTotal.findIndex((fs) => s.id === fs.id) === -1);
        allSitesTotal.push(...allSites1);
      }
    }

    return allSitesTotal;
  } catch (err) {
    throw AppError.fromGraphError(err);
  }
};
